import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * @file
 * @module Pages/AdminCreateSignupPage
 * @description AdminCreateSignupPage est une page qui permet à un administrateur de créer une URL de création de compte.
 * - L'administrateur entre le nom de l'entreprise et le SIRET, puis une URL est générée.
 * - Cette URL est ensuite affichée à l'administrateur, qui peut la copier dans le presse-papiers.
 * - L'URL contient le nom de l'entreprise et le SIRET chiffrés en AES et encodés pour être sûrs dans une URL.
 * - Lorsqu'un utilisateur accède à cette URL, le nom de l'entreprise et le SIRET sont déchiffrés et utilisés pour remplir le formulaire de création de compte.
 * - Cette page utilise la bibliothèque CryptoJS pour le chiffrement AES.
 * - Cette page utilise le hook useState de React pour gérer l'état des champs de saisie et de l'URL générée.
 * - Cette page utilise le hook useRef de React pour accéder au champ de saisie de l'URL et le sélectionner pour la copie.
 * - Cette page utilise la fonction encodeURIComponent de JavaScript pour encoder les données dans l'URL.
 * - Cette page utilise la méthode execCommand de JavaScript pour copier le contenu du champ de saisie dans le presse-papiers.
 */

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminCreateSignupPage = () => {
    const token = useSelector((state) => state.user.token);
    const [entreprise, setEntreprise] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const [siret, setSiret] = useState('');
    const [url, setUrl] = useState('');
    const [emails, setEmails] = useState('');

    const urlRef = useRef(null);

    const handleCopyUrl = () => {
        urlRef.current.select();
        document.execCommand('copy');
    };

    const hide = () => {
        setShowModal(false);
    };

    const handleCreateUrl = async () => {
        try {
            const response = await fetch(apiPrefix + 'signup-link', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ company: entreprise, siret }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.url) {
                setMessage(data.error || 'Impossible de créer le lien');
                setShowModal(true);
                return;
            }
            setUrl(data.url);
        } catch (error) {
            console.error(error);
            setMessage('Impossible de créer le lien');
            setShowModal(true);
        }
    };

    const handleSendInvite = async () => {
        const emailList = emails.split(',').map(email => email.trim());
        try {
            const responses = await Promise.all(emailList.map(email => 
                fetch(apiPrefix +'send-invite', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        email,
                        url,
                        entreprise
                    }),
                })
            ));

            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                setMessage('Invitations envoyées avec succès');
                setShowModal(true);
            } else {
                setMessage('Erreur lors de l\'envoi des invitations');
                setShowModal(true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="container-responsive section-padding">
            <div className="max-w-4xl mx-auto">
                <div className="card mb-8">
                    <h1 className="text-3xl font-bold text-primary mb-4">Créer une URL de création de compte</h1>
                    <p className="text-gray-600 mb-6">Entrez le nom de l'entreprise et le SIRET pour créer une URL de création de compte</p>
                    
                    <div className="space-y-4">
                        <div className="form-group">
                            <label className="form-label">
                                <i className="fas fa-building mr-2.5 text-primary"></i>
                                Nom de l'entreprise
                            </label>
                            <input 
                                type="text"
                                value={entreprise}
                                onChange={(e) => setEntreprise(e.target.value)}
                                placeholder="Entreprise"
                                className="form-input"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label className="form-label">
                                <i className="fas fa-hashtag mr-2.5 text-primary"></i>
                                SIRET
                            </label>
                            <input 
                                type="text"
                                value={siret}
                                onChange={(e) => setSiret(e.target.value)}
                                placeholder="SIRET"
                                className="form-input"
                            />
                        </div>
                        
                        <button 
                            onClick={handleCreateUrl}
                            className="btn-primary w-full"
                        >
                            <i className="fas fa-link mr-2"></i>
                            Créer URL
                        </button>
                    </div>
                </div>

                {url && (
                    <div className="space-y-6">
                        <div className="card">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">URL générée</h2>
                            <div className="flex gap-2">
                                <input
                                    ref={urlRef}
                                    value={url}
                                    readOnly
                                    className="form-input flex-1 font-mono text-sm"
                                />
                                <button 
                                    className="btn-secondary"
                                    onClick={handleCopyUrl}
                                >
                                    <i className="fas fa-copy mr-2"></i>
                                    Copier
                                </button>
                            </div>
                        </div>
                        
                        <div className="card">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Envoyer une invitation par e-mail</h2>
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-envelope mr-2.5 text-primary"></i>
                                    Adresses e-mail
                                </label>
                                <input 
                                    type="text"
                                    value={emails}
                                    onChange={(e) => setEmails(e.target.value)}
                                    placeholder="Emails (séparés par des virgules)"
                                    className="form-input"
                                />
                                <p className="text-sm text-gray-500 mt-1">Séparez plusieurs adresses par des virgules</p>
                            </div>
                            <button 
                                onClick={handleSendInvite}
                                className="btn-primary w-full"
                            >
                                <i className="fas fa-paper-plane mr-2"></i>
                                Envoyer les invitations
                            </button>
                        </div>
                        
                        {message && message.startsWith('Erreur') && (
                            <div className="alert alert-error">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {message}
                            </div>
                        )}
                        
            {showModal && (
                <div className="modal-overlay" onClick={hide}>
                    <div className="modal-content">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={hide}
                        >
                            ×
                        </button>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-primary mb-4">Information</h2>
                            <p className="text-gray-700 mb-6">{message}</p>
                            <button 
                                onClick={hide}
                                className="btn-primary"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminCreateSignupPage;